import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Card, CardBody, Col, Container, Row,
  Badge, Button, Input, Spinner, Alert,
} from "reactstrap";
import Select from "react-select";
import Breadcrumbs from "components/Common/Breadcrumb";

const BASE_URL = "https://hiflowapi.stechomeyazilim.info";

const ACCEPTED_STATUSES = ["Kabul Edildi", "Onaylandı"];
const DELIVERED_STATUSES = ["TESLIM_ETME", "Tamamlandı"];

const fmtTL = (n) =>
  (Number(n) || 0).toLocaleString("tr-TR", {
    style: "currency",
    currency: "TRY",
    minimumFractionDigits: 2,
  });

const driverLabel = (typeId) => {
  if (typeId === 2) return { text: "Sürücü", color: "info" };
  if (typeId === 4) return { text: "Çekici", color: "warning" };
  return { text: "-", color: "secondary" };
};

const SurucuHakedis = () => {
  document.title = "Sürücü Hakedişleri | LojGo Admin";

  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const isAdmin = authUser?.Users_Type_ID === 3;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("all"); // all | unpaid | paid | driver | tow
  const [selectedUserId, setSelectedUserId] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [usersRes, deliveriesRes, offersRes, paymentsRes] = await Promise.all([
        fetch(`${BASE_URL}/Users?$filter=Users_Type_ID eq 2 or Users_Type_ID eq 4&$top=5000`),
        fetch(
          `${BASE_URL}/Advert_DeliveryStatus?$filter=Advert_DeliveryStatus_Status eq 'TESLIM_ETME' or Advert_DeliveryStatus_Status eq 'Tamamlandı'&$top=10000`
        ),
        fetch(
          `${BASE_URL}/Advert_Offers?$filter=Offer_Status eq 'Kabul Edildi' or Offer_Status eq 'Onaylandı'&$expand=Advert_Expenses&$top=10000`
        ),
        fetch(`${BASE_URL}/Users_Payment?$top=10000`),
      ]);

      const [usersJ, delivJ, offerJ, payJ] = await Promise.all([
        usersRes.json(), deliveriesRes.json(), offersRes.json(), paymentsRes.json(),
      ]);

      const users = usersJ.value || [];
      const deliveries = delivJ.value || [];
      const offers = offerJ.value || [];
      const payments = payJ.value || [];

      // Set of delivered (driverId|advertId)
      const deliveredKey = new Set(
        deliveries
          .filter(d => DELIVERED_STATUSES.includes(d.Advert_DeliveryStatus_Status))
          .map(d => `${d.Users_ID}|${d.Advert_ID}`)
      );

      // Aggregate by driver
      const acc = new Map(); // Users_ID -> { jobCount, gross, expense, paid }
      const ensure = (uid) => {
        if (!acc.has(uid)) acc.set(uid, { jobCount: 0, gross: 0, expense: 0, paid: 0, pendingExpense: 0, quickJobs: 0 });
        return acc.get(uid);
      };

      offers.forEach((o) => {
        if (!ACCEPTED_STATUSES.includes(o.Offer_Status)) return;
        if (!deliveredKey.has(`${o.Users_ID}|${o.Advert_ID}`)) return;
        const a = ensure(o.Users_ID);
        a.jobCount += 1;
        // Advert_Offers.Price = sürücünün vermiş olduğu net teklif; ekstra kesinti yok
        a.gross += Number(o.Price) || 0;
        if (o.Quickly) a.quickJobs += 1;
        const exps = o.Advert_Expenses || [];
        exps.forEach(e => {
          const v = Number(e.Total_Expense) || 0;
          if (e.Is_Approved) a.expense += v;
          else a.pendingExpense += v;
        });
        // If no Advert_Expenses row, fall back to offer's Travel_Expenses
        if (exps.length === 0) {
          a.expense += Number(o.Travel_Expenses) || 0;
        }
      });

      payments.forEach((p) => {
        if (!p.Users_ID) return;
        const a = ensure(p.Users_ID);
        a.paid += Number(p.Price) || 0;
      });

      const usersById = new Map(users.map(u => [u.ID, u]));

      const list = [];
      acc.forEach((v, uid) => {
        const u = usersById.get(uid);
        if (!u) return;
        const earned = v.gross + v.expense;
        const balance = earned - v.paid;
        list.push({
          userId: uid,
          name: u.Users_NameSurname || "-",
          phone: u.Users_PhoneNumber || "-",
          email: u.Users_Email || "-",
          typeId: u.Users_Type_ID,
          jobCount: v.jobCount,
          quickJobs: v.quickJobs,
          gross: v.gross,
          expense: v.expense,
          pendingExpense: v.pendingExpense,
          earned,
          paid: v.paid,
          balance,
        });
      });

      list.sort((a, b) => b.balance - a.balance);
      setRows(list);
    } catch (e) {
      console.error(e);
      setError("Veriler yüklenirken bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (isAdmin) loadData(); }, [isAdmin, loadData]);

  const userOptions = useMemo(() => {
    const opts = rows
      .map(r => {
        const lbl = driverLabel(r.typeId);
        return {
          value: r.userId,
          label: `${r.name} · ${lbl.text}${r.phone && r.phone !== "-" ? ` · ${r.phone}` : ""}`,
          typeId: r.typeId,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label, "tr"));
    return opts;
  }, [rows]);

  const selectedOption = useMemo(
    () => userOptions.find(o => o.value === selectedUserId) || null,
    [userOptions, selectedUserId]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLocaleLowerCase("tr");
    return rows.filter(r => {
      if (selectedUserId && r.userId !== selectedUserId) return false;
      if (tab === "unpaid" && r.balance <= 0.009) return false;
      if (tab === "paid"   && r.balance >  0.009) return false;
      if (tab === "driver" && r.typeId !== 2) return false;
      if (tab === "tow"    && r.typeId !== 4) return false;
      if (!q) return true;
      return (
        (r.name || "").toLocaleLowerCase("tr").includes(q) ||
        (r.phone || "").includes(q) ||
        (r.email || "").toLocaleLowerCase("tr").includes(q) ||
        String(r.userId).includes(q)
      );
    });
  }, [rows, tab, search, selectedUserId]);

  const totals = useMemo(() => {
    const t = { earned: 0, paid: 0, balance: 0, jobs: 0, drivers: filtered.length };
    filtered.forEach(r => {
      t.earned  += r.earned;
      t.paid    += r.paid;
      t.balance += r.balance;
      t.jobs    += r.jobCount;
    });
    return t;
  }, [filtered]);

  if (!isAdmin) {
    return (
      <div className="page-content">
        <Container fluid>
          <Alert color="danger">Bu sayfayı görüntüleme yetkiniz yok.</Alert>
        </Container>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Ödeme" breadcrumbItem="Sürücü Hakedişleri" />

          {error && <Alert color="danger">{error}</Alert>}

          {/* Özet kartları */}
          <Row className="mb-3">
            <Col md={3}>
              <Card><CardBody>
                <p className="text-muted mb-1 fw-medium">Listelenen Sürücü</p>
                <h4 className="mb-0">{totals.drivers}</h4>
                <small className="text-muted">{totals.jobs} teslim edilmiş iş</small>
              </CardBody></Card>
            </Col>
            <Col md={3}>
              <Card><CardBody>
                <p className="text-muted mb-1 fw-medium">Toplam Hakediş</p>
                <h4 className="mb-0 text-info">{fmtTL(totals.earned)}</h4>
                <small className="text-muted">Teslim edilen işlerin tutarı</small>
              </CardBody></Card>
            </Col>
            <Col md={3}>
              <Card><CardBody>
                <p className="text-muted mb-1 fw-medium">Ödenmiş</p>
                <h4 className="mb-0 text-success">{fmtTL(totals.paid)}</h4>
                <small className="text-muted">Ödeme toplamı</small>
              </CardBody></Card>
            </Col>
            <Col md={3}>
              <Card><CardBody>
                <p className="text-muted mb-1 fw-medium">Kalan Borç (Bizden)</p>
                <h4 className={`mb-0 ${totals.balance > 0 ? "text-danger" : "text-success"}`}>{fmtTL(totals.balance)}</h4>
                <small className="text-muted">Sürücülere ödenecek</small>
              </CardBody></Card>
            </Col>
          </Row>

          <Card>
            <CardBody>
              {/* Filtre çubuğu */}
              <Row className="mb-3 g-2 align-items-center">
                <Col md={12} lg={5}>
                  <Select
                    isClearable
                    placeholder="Kullanıcı seç ile filtrele…"
                    value={selectedOption}
                    onChange={(opt) => setSelectedUserId(opt?.value || null)}
                    options={userOptions}
                    noOptionsMessage={() => "Sürücü bulunamadı"}
                    classNamePrefix="select2-selection"
                    isDisabled={loading || rows.length === 0}
                  />
                </Col>
                <Col md={8} lg={5}>
                  <div className="btn-group flex-wrap" role="group">
                    {[
                      { k: "all",    label: "Tümü" },
                      { k: "unpaid", label: "Ödenecek" },
                      { k: "paid",   label: "Kapanan" },
                      { k: "driver", label: "Sürücü" },
                      { k: "tow",    label: "Çekici" },
                    ].map(t => (
                      <Button
                        key={t.k}
                        color={tab === t.k ? "primary" : "light"}
                        size="sm"
                        onClick={() => setTab(t.k)}
                      >
                        {t.label}
                      </Button>
                    ))}
                  </div>
                </Col>
                <Col md={4} lg={2} className="ms-md-auto">
                  <Input
                    type="search"
                    placeholder="Hızlı arama…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    bsSize="sm"
                  />
                </Col>
              </Row>

              <div className="d-flex justify-content-between align-items-center mb-2">
                <div>
                  {(selectedUserId || search || tab !== "all") && (
                    <Button
                      color="link"
                      size="sm"
                      className="p-0 text-muted"
                      onClick={() => { setSelectedUserId(null); setSearch(""); setTab("all"); }}
                    >
                      <i className="bx bx-x me-1" />Filtreleri temizle
                    </Button>
                  )}
                </div>
                <Button color="light" size="sm" onClick={loadData} disabled={loading}>
                  <i className="bx bx-refresh" /> Yenile
                </Button>
              </div>

              {loading ? (
                <div className="text-center py-5"><Spinner /></div>
              ) : filtered.length === 0 ? (
                <div className="text-center py-5 text-muted">Kayıt bulunamadı.</div>
              ) : (
                <div className="table-responsive">
                  <table className="table align-middle table-nowrap mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Sürücü</th>
                        <th>Tip</th>
                        <th className="text-end">Teslim İşi</th>
                        <th className="text-end">Hizmet Bedeli</th>
                        <th className="text-end">Onaylı Masraf</th>
                        <th className="text-end">Toplam Hakediş</th>
                        <th className="text-end">Ödenmiş</th>
                        <th className="text-end">Bakiye</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map(r => {
                        const lbl = driverLabel(r.typeId);
                        return (
                          <tr key={r.userId}>
                            <td>
                              <div className="fw-semibold">{r.name}</div>
                              <small className="text-muted">{r.phone} · #{r.userId}</small>
                            </td>
                            <td><Badge color={lbl.color}>{lbl.text}</Badge></td>
                            <td className="text-end">
                              {r.jobCount}
                              {r.quickJobs > 0 && (
                                <div><small className="text-warning">{r.quickJobs} hızlı</small></div>
                              )}
                            </td>
                            <td className="text-end">{fmtTL(r.gross)}</td>
                            <td className="text-end">
                              {fmtTL(r.expense)}
                              {r.pendingExpense > 0 && (
                                <div><small className="text-warning">+{fmtTL(r.pendingExpense)} onay bekliyor</small></div>
                              )}
                            </td>
                            <td className="text-end fw-semibold">{fmtTL(r.earned)}</td>
                            <td className="text-end text-success">{fmtTL(r.paid)}</td>
                            <td className="text-end">
                              <span className={`fw-bold ${r.balance > 0.009 ? "text-danger" : "text-success"}`}>
                                {fmtTL(r.balance)}
                              </span>
                            </td>
                            <td className="text-end">
                              <Link to={`/surucu-hakedis/${r.userId}`} className="btn btn-sm btn-primary">
                                Detay
                              </Link>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default SurucuHakedis;
